<template>
  <div class="asc-add-parameter-to-custom-alerts-dialog">
    <asc-dialog
      text="Add Parameter"
      :show-dialog="showDialog"
      :on-close="handleClose"
      :width="1045"
      secondary-button-text="Cancel"
      :on-secondary="handleClose"
      primary-button-text="Add To Custom Analytic"
      :on-primary="handlePrimaryClick"
      :primary-available="selectedDataElementId !== null"
    >
      <template v-if="showDialog">
        <asc-spinner-wrapper :loading="loading">
          <p class="subtitle">{{ getSubtitleText }}</p>
          <v-row class="filter-options" no-gutters>
            <v-col
              v-if="hasMultipleAirlines"
              cols="6"
              md="4"
              sm="5"
              class="mr-md-6 mr-sm-6 mb-md-0 mb-sm-6"
            >
              <asc-autocomplete-selector
                v-if="alertType === 'acmf'"
                v-model="selectedAirlineId"
                :items="airlines"
                no-array
                label="Airline"
                drop-down-size-matches
                fixed-position
                @input="handleChangeAirlineId"
              ></asc-autocomplete-selector>
            </v-col>
            <v-col cols="6" md="4" sm="5" class="mb-md-0 mb-sm-6">
              <asc-autocomplete-selector
                v-if="alertType === 'acmf'"
                v-model="selectedReportTypeId"
                :items="getReportTypes"
                no-array
                label="Report Type"
                drop-down-size-matches
                fixed-position
                @input="handleChangeReportType"
              ></asc-autocomplete-selector>
            </v-col>
          </v-row>
          <asc-selection-grid
            :values="getParameterList"
            :show-rows-per-page="false"
            :full-columns="true"
            :selected="innerVal"
            :page-size="defaultPageSize"
            :column-defs="columnDefs"
            :no-data-message="getNoDataMessage"
            :get-row-id="params => params.data.id"
            row-selection="single"
            grid-size="auto"
            @onRowSelected="handleSelectionChanged"
          ></asc-selection-grid>
        </asc-spinner-wrapper>
      </template>
    </asc-dialog>
  </div>
</template>

<script>
import { AscDialogMixin } from '@/components/mixins'
import AscDialog from '@/components/atoms/AscDialog'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscSelectionGrid from '@/components/molecules/AscSelectionGrid'
import AscAutocompleteSelector from './AscAutocompleteSelector'

import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'AscCustomAlertsAddParameterDialog',

  components: {
    AscDialog,
    AscSpinnerWrapper,
    AscSelectionGrid,
    AscAutocompleteSelector,
  },

  mixins: [AscDialogMixin],

  props: {
    value: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    parameters: {
      type: Array,
      default: () => [],
    },
    onApply: {
      type: Function,
      default: () => {},
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    airlines: {
      type: Array,
      default: () => [],
    },
    reportTypes: {
      type: Array,
      default: () => [],
    },
    alertType: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      defaultPageSize: 25,
      innerVal: [],
      selectedAirlineId: null,
      selectedAirlineName: null,
      selectedAirline: null,
      selectedReportTypeId: null,
      selectedReportTypeName: null,
      selectedDataElementId: null,
      selectedDataElementAirlineId: null,
      selectedDataElementName: null,
      selectedDataElementRawName: null,
      selectedDataElementAirlineName: null,
      selectedPlatformName: null,
    }
  },

  computed: {
    hasMultipleAirlines() {
      return this.airlines && this.airlines.length > 1
    },
    getParameterRemovalDialogTitle() {
      return 'Remove Parameter Confirmation'
    },
    getParameterRemovalDialogText() {
      return `You are about to remove this parameter from your analytic. This will reset any customizations you've made.`
    },
    getNoDataMessage() {
      return `Select ${
        this.hasMultipleAirlines ? 'an airline and' : 'a'
      } report type to see parameter options.`
    },
    getSubtitleText() {
      let textString
      if (this.alertType === 'acmf' && this.hasMultipleAirlines) {
        textString =
          'Select an airline and report type then choose a parameter to add to the custom analytic'
      } else if (this.alertType === 'acmf' && !this.hasMultipleAirlines) {
        textString =
          'Select a report type and then choose a parameter to add to the custom analytic'
      } else {
        textString = 'Select a parameter to add to the custom analytic'
      }
      return textString
    },
    getReportTypes() {
      if (this.selectedAirline) {
        const validMasterModels = this.selectedAirline.items.map(
          airline => airline.masterModelId,
        )
        const filteredReports = this.reportTypes.filter(report => {
          return validMasterModels.includes(report.masterModelId)
        })
        return filteredReports
      }
      return this.reportTypes
    },
    getParameterList() {
      const mappedParameters = this.parameters.map(parameterObj => {
        if (this.alertType === 'acmf') {
          return {
            id: `${parameterObj.acmfDataElementId}-${
              parameterObj.selectedAirlineId
            }-${uuidv4()}`,
            parameter: parameterObj.shortName,
            rawName: parameterObj.parameterName,
            reportType: this.selectedReportTypeName,
            system: parameterObj.system,
            airline: this.selectedAirlineName,
            platform: parameterObj.masterModel,
            unit:
              parameterObj.unit === null || parameterObj.unit === ''
                ? 'N/A'
                : parameterObj.unit,
          }
        } else {
          return {
            id: `${parameterObj.dataElementId}-${
              parameterObj.airlineId
            }-${uuidv4()}`,
            parameter:
              parameterObj.name?.length > 0
                ? parameterObj.name
                : parameterObj.rawName,
            // still need raw name from API
            rawName: parameterObj.rawName,
            system: parameterObj.system,
            airline: parameterObj.airlineName,
            platform: parameterObj.masterModelName,
            unit:
              parameterObj.units === null || parameterObj.units === ''
                ? 'N/A'
                : parameterObj.units,
          }
        }
      })
      if (
        this.alertType === 'acmf' &&
        (this.selectedReportTypeId === null || this.selectedAirlineId === null)
      ) {
        return []
      } else {
        return mappedParameters
      }
    },
    checkValidReportType() {
      if (this.selectedReportTypeId) {
        const filteredReportIds = this.getReportTypes.map(report => report.id)
        return filteredReportIds.includes(this.selectedReportTypeId)
      }
      return false
    },
  },

  watch: {
    selectedParameter(val) {
      this.innerVal = val || []
    },
    airlines(val) {
      if (val.length === 1) {
        this.handleChangeAirlineId(val[0].value)
      }
    },
  },

  created() {
    this.checkForIdAndReportType()
  },

  methods: {
    handleSelectionChanged(selectedRow) {
      if (selectedRow.selected) {
        const rowIdParts = selectedRow.data.id.split('-')
        // the row ID in the table is stored like this: dataElementId-airlineId-guid
        // this was to ensure unique row IDs since data element ids and airline id
        // combinations can be repeated
        this.selectedDataElementId = rowIdParts[0]
        // for snapshot alets, the airline is defined in the filter before you select
        // a parameter.  For fullflight, the airline is shown in the parameter grid itself
        if (this.alertType === 'acmf') {
          this.selectedDataElementAirlineId = this.selectedAirlineId
        } else {
          this.selectedDataElementAirlineId = rowIdParts[1]
        }
        this.selectedDataElementName = selectedRow.data.parameter
        this.selectedDataElementUnit = selectedRow.data.unit
        this.selectedDataElementRawName = selectedRow.data.rawName
        this.selectedAirlineName =
          this.airlines.length === 1
            ? this.selectedAirlineName
            : selectedRow.data.airline
        this.selectedPlatformName = selectedRow.data.platform
      }
    },
    handlePrimaryClick() {
      this.onApply({
        dataElementId: this.selectedDataElementId,
        dataElementName: this.selectedDataElementName,
        dataElementRawName: this.selectedDataElementRawName,
        airlineId: this.selectedDataElementAirlineId,
        unit: this.selectedDataElementUnit,
        airlineName: this.selectedAirlineName,
        platformName: this.selectedPlatformName,
      })
      this.selectedAirline = null
      this.selectedAirlineId = null
      this.selectedAirlineName = null
      this.selectedReportTypeId = null
      this.selectedReportTypeName = null
      this.selectedDataElementId = null
      this.selectedDataElementAirlineId = null
      this.selectedDataElementName = null
      this.selectedDataElementRawName = null
      this.selectedDataElementUnit = null
    },
    handleClose() {
      if (this.onClose) {
        this.selectedAirline = null
        this.selectedAirlineId = null
        this.selectedAirlineName = null
        this.selectedReportTypeId = null
        this.selectedReportTypeName = null
        this.selectedDataElementId = null
        this.selectedDataElementAirlineId = null
        this.selectedDataElementName = null
        this.selectedDataElementRawName = null
        this.selectedDataElementUnit = null
        this.onClose()
      }
    },
    handleChangeAirlineId(value) {
      const airlineName = this.airlines.filter(airlineObj => {
        return airlineObj.value === value
      })
      this.selectedAirline = airlineName[0]
      this.selectedAirlineName = airlineName[0].text
      this.$emit('setSnapshotAirlineId', value)
      this.checkForIdAndReportType()
    },
    handleChangeReportType(value) {
      const reportType = this.reportTypes.filter(reportTypeObj => {
        return reportTypeObj.id === value
      })
      this.$emit('setSnapshotReportType', value)
      this.selectedReportTypeName = reportType[0].text
      this.checkForIdAndReportType()
    },
    checkForIdAndReportType() {
      if (this.airlines.length === 1) {
        this.selectedAirlineId = this.airlines[0].value
        this.selectedAirlineName = this.airlines[0].text
        this.selectedAirline = this.airlines[0]
        this.$emit('setSnapshotAirlineId', this.airlines[0].value)
      }
      if (
        this.checkValidReportType &&
        this.selectedAirlineId !== null &&
        this.selectedReportTypeId !== null
      ) {
        this.$emit('airlineReportSelected', {
          reportType: this.selectedReportTypeId,
          airlineId: this.selectedAirlineId,
        })
      }
    },
  },
}
</script>
<style lang="scss">
.asc-add-parameter-to-custom-alerts-dialog {
  p.subtitle {
    margin-top: 16px;
    margin-bottom: 13px;
  }

  .asc-selection-grid {
    &--hasFilters {
      .ag-layout-auto-height {
        min-height: 280px;
      }
    }
  }
}

@media only screen and (min-width: 960px) {
  .asc-add-parameter-to-custom-alerts-dialog {
    .filter-options {
      margin-bottom: 22px;

      & > div {
        max-width: 290px !important;
      }
    }
  }
}
</style>
